import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Plus, Video, Mail, ExternalLink, AlertCircle, Info, FileText, MessageSquare, Edit, RotateCcw, Download, Play, Trash2, Zap, Loader2 } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';
import { InterviewWizard } from '@/components/interviews/InterviewWizard';
import { InterviewInviteModal } from '@/components/interviews/InterviewInviteModal';
import { TranscriptViewerModal } from '@/components/interviews/TranscriptViewerModal';
import { TranscriptEditorModal } from '@/components/interviews/TranscriptEditorModal';
import { VideoViewerModal } from '@/components/interviews/VideoViewerModal';
import { ArticleGenerationModal } from '@/components/interviews/ArticleGenerationModal';
import { QuestionAnswerExtractionPanel } from '@/components/interviews/QuestionAnswerExtractionPanel';

interface Interview {
  id: string;
  site_id: string;
  expert_name: string;
  expert_title: string;
  expert_company: string;
  expert_email?: string;
  expert_photo_url?: string;
  journalist_id: string;
  journalist_name: string;
  journalist_photo_url?: string;
  approach: string;
  status: string;
  transcript_status?: string;
  analysis_status?: string;
  generated_questions?: any[];
  selected_questions?: any[];
  briefing_content?: string;
  share_link?: string;
  token_expires_at?: string;
  created_at: string;
  recording_path?: string;
  recording_size?: number;
  transcript_text?: string;
  language?: string;
  video_url?: string;
  audio_url?: string;
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-500',
  questions_generated: 'bg-blue-500',
  briefing_ready: 'bg-indigo-500',
  briefing_viewed: 'bg-indigo-600',
  setup_started: 'bg-purple-500',
  setup_completed: 'bg-purple-600',
  ready: 'bg-orange-500',
  invitation_sent: 'bg-purple-400',
  scheduled: 'bg-orange-400',
  recording_started: 'bg-yellow-500',
  recording_paused: 'bg-yellow-600',
  in_progress: 'bg-yellow-500',
  recorded: 'bg-green-500',
  processing: 'bg-cyan-500',
  completed: 'bg-emerald-500',
  cancelled: 'bg-red-500'
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  questions_generated: 'Questions Generated',
  briefing_ready: 'Briefing Ready',
  briefing_viewed: 'Briefing Viewed',
  setup_started: 'Setup Started',
  setup_completed: 'Setup Completed',
  ready: 'Ready to Record',
  invitation_sent: 'Invitation Sent',
  scheduled: 'Scheduled',
  recording_started: 'Recording Started',
  recording_paused: 'Recording Paused',
  in_progress: 'In Progress',
  recorded: 'Recorded',
  processing: 'Processing',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

const approachLabels: Record<string, string> = {
  product_deep_dive: 'Product Deep Dive',
  product_comparison: 'Product Comparison',
  category: 'Category Overview',
  selected_questions: 'Custom Questions',
  trending_topic: 'Trending Topic',
  expert_matched: 'Expert Knowledge Match',
  question_wheel: 'From Question Wheel',
  business_story: 'Business Story'
};

export default function ContentInterviews() {
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperAdmin = user?.role === 'super_admin';
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);

  // URL-driven initial values for the wizard (from ConvIntel dashboard link)
  const initialExpertId = searchParams.get('expertId') || undefined;
  const initialApproach = searchParams.get('approach') || undefined;

  // Auto-open wizard if URL params are present
  useEffect(() => {
    if (initialExpertId && !showWizard) {
      setShowWizard(true);
    }
  }, [initialExpertId]);
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [inviteModalInterview, setInviteModalInterview] = useState<Interview | null>(null);
  const [transcriptViewerInterview, setTranscriptViewerInterview] = useState<Interview | null>(null);
  const [videoViewerInterview, setVideoViewerInterview] = useState<Interview | null>(null);
  const [articleGenerationInterview, setArticleGenerationInterview] = useState<Interview | null>(null);
  const [transcriptEditorInterview, setTranscriptEditorInterview] = useState<Interview | null>(null);
  const [expandedQAPanel, setExpandedQAPanel] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // "I Feel Lucky" instant interview state
  const [instantPrompt, setInstantPrompt] = useState('');
  const [instantLoading, setInstantLoading] = useState(false);

  useEffect(() => {
    if (selectedSite?.id) {
      fetchInterviews();
    }
  }, [selectedSite, refreshKey]);

  const fetchInterviews = async () => {
    if (!selectedSite?.id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(`/api/interviews?siteId=${selectedSite.id}`);

      if (!response.ok) {
        throw new Error('Failed to fetch interviews');
      }

      const data = await response.json();
      setInterviews(data.interviews || []);
    } catch (err) {
      console.error('Error fetching interviews:', err);
      setError(err instanceof Error ? err.message : 'Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedInterview(null);
    setShowWizard(true);
  };

  const handleWizardClose = () => {
    setShowWizard(false);
    setSelectedInterview(null);
  };

  const handleWizardSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleEditInterview = (interview: Interview) => {
    setSelectedInterview(interview);
    setShowWizard(true);
  };

  const handleSendInvite = (interview: Interview) => {
    setInviteModalInterview(interview);
  };

  const handleStartInterview = async (interview: Interview) => {
    try {
      // Generate admin token and get interview URL
      const response = await authFetch(`/api/interviews/${interview.id}/admin-start`, {
        method: 'POST'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to start interview');
      }

      const data = await response.json();

      // Redirect to interview page with token
      window.location.href = data.interviewUrl;

    } catch (err) {
      console.error('Error starting interview:', err);
      setError(err instanceof Error ? err.message : 'Failed to start interview');
    }
  };

  const handleInstantInterview = async (selfInterview = false) => {
    if (!selectedSite?.id || !instantPrompt.trim()) return;

    try {
      setInstantLoading(true);
      setError(null);

      const response = await authFetch('/api/interviews/instant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          prompt: instantPrompt.trim(),
          language: selectedSite.language || 'en',
          selfInterview,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create instant interview');
      }

      const data = await response.json();

      // Navigate to the interview
      window.location.href = data.shareLink;
    } catch (err) {
      console.error('Error creating instant interview:', err);
      setError(err instanceof Error ? err.message : 'Failed to create instant interview');
    } finally {
      setInstantLoading(false);
    }
  };

  const handleResetInterview = async (interview: Interview) => {
    if (!confirm(`Reset interview with ${interview.expert_name}? This will allow you to re-record the interview.`)) {
      return;
    }

    try {
      const response = await authFetch(`/api/interviews/${interview.id}/reset`, {
        method: 'POST'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to reset interview');
      }

      // Refresh interviews list
      setRefreshKey((prev) => prev + 1);

    } catch (err) {
      console.error('Error resetting interview:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset interview');
    }
  };

  const handleDeleteInterview = async (interview: Interview) => {
    if (!confirm(`Permanently delete interview with ${interview.expert_name}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await authFetch(`/api/interviews/${interview.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete interview');
      }

      // Refresh interviews list
      setRefreshKey((prev) => prev + 1);

    } catch (err) {
      console.error('Error deleting interview:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete interview');
    }
  };

  if (!selectedSite) {
    return (
      <div className="container mx-auto py-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Please select a site to manage content interviews
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Content Interviews</h1>
          <p className="text-muted-foreground">
            Create and manage expert interviews with AI journalists
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          New Interview
        </Button>
      </div>

      {/* I Feel Lucky — Instant Interview */}
      <Card className="border-primary/30 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Instant Interview
          </CardTitle>
          <CardDescription>
            Type a topic and start interviewing in seconds. The system picks the best journalist and generates gap-aware questions automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="What do you want to talk about? e.g. &quot;marathon running shoes&quot;"
              value={instantPrompt}
              onChange={(e) => setInstantPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && instantPrompt.trim()) {
                  handleInstantInterview(false);
                }
              }}
              disabled={instantLoading}
              className="flex-1"
            />
            <Button
              onClick={() => handleInstantInterview(false)}
              disabled={instantLoading || !instantPrompt.trim()}
            >
              {instantLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              {instantLoading ? 'Setting up...' : 'Invite an Expert'}
            </Button>
          </div>

          {/* Self-interview CTA — dedicated entry point */}
          <div className="flex items-center gap-3 pt-2 border-t border-primary/10">
            <div className="flex-1 text-sm">
              <div className="font-medium">Know the answers yourself?</div>
              <div className="text-xs text-muted-foreground">
                Skip invitations and start recording right away. The journalist interviews you — transcript is attributed to you as the expert.
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={() => handleInstantInterview(true)}
              disabled={instantLoading || !instantPrompt.trim()}
              title="Interview yourself — no external expert needed"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Interview Myself
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5" />
            About Content Interviews
          </CardTitle>
          <CardDescription>
            Conduct professional interviews with product experts to generate SEO-optimized content
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm">
            The interview workflow allows you to:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>Define interview topics and select AI journalist personas</li>
            <li>Generate contextual questions using GPT-4</li>
            <li>Send interview invitations to external experts</li>
            <li>Conduct real-time video/audio interviews (or record yourself)</li>
            <li>Extract Q&A snippets for chatbots and product pages</li>
            <li>Generate SEO-optimized articles from transcripts</li>
          </ul>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Interviews List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Your Interviews
          </CardTitle>
          <CardDescription>
            Manage all content interviews for {selectedSite.domain}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : interviews.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No interviews yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first interview to get started
              </p>
              <Button onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Interview
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {interviews.map((interview) => (
                <Card key={interview.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex gap-4 flex-1">
                        {/* Expert Photo */}
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-indigo-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                          {interview.expert_photo_url ? (
                            <img
                              src={interview.expert_photo_url}
                              alt={interview.expert_name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            interview.expert_name.charAt(0).toUpperCase()
                          )}
                        </div>

                        {/* Interview Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-lg">{interview.expert_name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {interview.expert_title} at {interview.expert_company}
                              </p>
                            </div>
                            <Badge className={statusColors[interview.status]}>
                              {statusLabels[interview.status]}
                            </Badge>
                          </div>

                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                            <div className="flex items-center gap-1">
                              <img
                                src={interview.journalist_photo_url || ''}
                                alt={interview.journalist_name}
                                className="w-5 h-5 rounded-full object-cover"
                              />
                              <span>{interview.journalist_name}</span>
                            </div>
                            <span>•</span>
                            <span>{approachLabels[interview.approach]}</span>
                            {interview.selected_questions && (
                              <>
                                <span>•</span>
                                <span>
                                  {Array.isArray(interview.selected_questions)
                                    ? interview.selected_questions.length
                                    : typeof interview.selected_questions === 'string'
                                    ? JSON.parse(interview.selected_questions).length
                                    : 0} questions
                                </span>
                              </>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 flex-wrap">
                            {/* Edit button for draft interviews */}
                            {['draft', 'questions_generated', 'briefing_ready'].includes(interview.status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditInterview(interview)}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                {interview.status === 'draft' ? 'Continue Draft' : 'Edit Interview'}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStartInterview(interview)}
                              disabled={
                                !isSuperAdmin &&
                                !['briefing_ready', 'briefing_viewed', 'setup_started', 'setup_completed', 'recording_started', 'recording_paused', 'ready'].includes(interview.status)
                              }
                            >
                              <Video className="w-4 h-4 mr-1" />
                              {['briefing_viewed', 'setup_started', 'setup_completed', 'recording_started', 'recording_paused', 'ready'].includes(interview.status) ? 'Continue Interview' : 'Start Interview'}
                            </Button>
                            {isSuperAdmin && ['recorded', 'processing', 'completed'].includes(interview.status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleResetInterview(interview)}
                                className="text-orange-600 dark:text-orange-400 border-orange-300 dark:border-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950"
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Reset Interview
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteInterview(interview)}
                              className="text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendInvite(interview)}
                              disabled={['recorded', 'processing', 'completed', 'cancelled'].includes(interview.status)}
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              {interview.status === 'briefing_ready' ? 'Send Invite' : 'Resend Invite'}
                            </Button>
                            {interview.share_link && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => window.open(interview.share_link, '_blank')}
                              >
                                <ExternalLink className="w-4 h-4 mr-1" />
                                View Link
                              </Button>
                            )}
                            {(interview.transcript_status === 'completed' || interview.transcript_status === 'realtime_completed') && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setTranscriptViewerInterview(interview)}
                                  className="text-green-600 dark:text-green-400 border-green-300 dark:border-green-600 hover:bg-green-50 dark:hover:bg-green-950"
                                >
                                  <MessageSquare className="w-4 h-4 mr-1" />
                                  View Transcript
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setTranscriptEditorInterview(interview)}
                                  className="text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit Transcript
                                </Button>
                              </>
                            )}
                            {interview.recording_path && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setVideoViewerInterview(interview)}
                                  className="text-indigo-600 dark:text-indigo-400 border-indigo-300 dark:border-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950"
                                >
                                  <Play className="w-4 h-4 mr-1" />
                                  View Video
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => window.open(`/api/expert/interview/video/${interview.id}`, '_blank')}
                                  className="text-purple-600 dark:text-purple-400 border-purple-300 dark:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Download ({(interview.recording_size / (1024 * 1024)).toFixed(1)} MB)
                                </Button>
                              </>
                            )}
                            {(interview.transcript_status === 'completed' || interview.transcript_status === 'realtime_completed') && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setArticleGenerationInterview(interview)}
                                  className="text-cyan-600 dark:text-cyan-400 border-cyan-300 dark:border-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-950"
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  Generate Article
                                </Button>
                                <Button
                                  size="sm"
                                  variant={expandedQAPanel === interview.id ? "default" : "outline"}
                                  onClick={() => setExpandedQAPanel(expandedQAPanel === interview.id ? null : interview.id)}
                                  className={expandedQAPanel === interview.id ? "bg-blue-600 hover:bg-blue-700" : "text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"}
                                >
                                  <MessageSquare className="w-4 h-4 mr-1" />
                                  {expandedQAPanel === interview.id ? 'Hide Q&A Extraction' : 'Extract Answers'}
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>

                  {/* Q&A Extraction Panel */}
                  {expandedQAPanel === interview.id && (
                    <div className="border-t bg-muted/30 p-6">
                      <QuestionAnswerExtractionPanel
                        interview={interview}
                        onSuccess={() => setRefreshKey((prev) => prev + 1)}
                      />
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Interview Wizard Dialog */}
      <InterviewWizard
        open={showWizard}
        onClose={() => {
          handleWizardClose();
          // Clear URL params when wizard is closed
          if (searchParams.has('expertId')) {
            searchParams.delete('expertId');
            searchParams.delete('approach');
            setSearchParams(searchParams, { replace: true });
          }
        }}
        interview={selectedInterview}
        onSuccess={handleWizardSuccess}
        initialExpertId={initialExpertId}
        initialApproach={initialApproach}
      />

      {/* Interview Invite Modal */}
      {inviteModalInterview && (
        <InterviewInviteModal
          interview={inviteModalInterview}
          open={!!inviteModalInterview}
          onClose={() => setInviteModalInterview(null)}
          onSuccess={() => {
            setInviteModalInterview(null);
            setRefreshKey((prev) => prev + 1);
          }}
        />
      )}

      {/* Transcript Viewer Modal */}
      {transcriptViewerInterview && (
        <TranscriptViewerModal
          interviewId={transcriptViewerInterview.id}
          expertName={transcriptViewerInterview.expert_name}
          open={!!transcriptViewerInterview}
          onClose={() => setTranscriptViewerInterview(null)}
        />
      )}

      {/* Video Viewer Modal */}
      {videoViewerInterview && (
        <VideoViewerModal
          interviewId={videoViewerInterview.id}
          expertName={videoViewerInterview.expert_name}
          open={!!videoViewerInterview}
          onClose={() => setVideoViewerInterview(null)}
          recordingSize={videoViewerInterview.recording_size}
        />
      )}

      {/* Article Generation Modal */}
      {articleGenerationInterview && (
        <ArticleGenerationModal
          interview={articleGenerationInterview}
          open={!!articleGenerationInterview}
          onClose={() => setArticleGenerationInterview(null)}
          onSuccess={(article) => {
            console.log('[ContentInterviews] Article generated:', article);
            setArticleGenerationInterview(null);
            setRefreshKey((prev) => prev + 1);
          }}
        />
      )}

      {/* Transcript Editor Modal */}
      {transcriptEditorInterview && (
        <TranscriptEditorModal
          interview={transcriptEditorInterview}
          open={!!transcriptEditorInterview}
          onClose={() => setTranscriptEditorInterview(null)}
          onSuccess={() => {
            console.log('[ContentInterviews] Transcript updated');
            setTranscriptEditorInterview(null);
            setRefreshKey((prev) => prev + 1);
          }}
        />
      )}
    </div>
  );
}
